/* eslint-disable react/no-danger */
/* eslint-disable quotes */
/* eslint-disable react/no-danger */
/* eslint-disable object-shorthand */
/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */

import {
  CSSProperties,
  forwardRef,
  MouseEventHandler,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { EmojiPicker } from 'components/EmojiPicker';
import { PickerPositioner } from 'components/EmojiPicker/PickerPositioner';
import { Button, Progress } from 'antd';
import { AxiosResponse } from 'axios';
import Lightbox, { LightboxProps } from 'yet-another-react-lightbox';
import Counter from 'yet-another-react-lightbox/dist/plugins/counter';
import Thumbnails from 'yet-another-react-lightbox/dist/plugins/thumbnails';
import Zoom from 'yet-another-react-lightbox/dist/plugins/zoom';
import 'yet-another-react-lightbox/dist/styles.css';
import 'yet-another-react-lightbox/dist/plugins/counter/counter.css';
import 'yet-another-react-lightbox/dist/plugins/thumbnails/thumbnails.css';
import { fromUint8Array } from 'js-base64';
import * as Y from 'yjs';
import ReactQuill from 'react-quill';
import Quill from 'utils/quill-configs';
import { TextProps } from 'antd/lib/typography/Text';
import AutoLinks from 'quill-auto-links';
import 'quill-mention';
import apiRequests from 'utils/api';
import asyncErrorHandler from 'utils/asyncErrorHandler';
import { debounceAsync } from 'utils/debounce';
import { isImgElement } from 'utils/guardTypes';
import apiRoutes from 'config/apiRoute';
import type { UploadFileApi } from 'types/upload-type';
import type { CollaborativeEditorOptions } from 'types/richTextEditor';
import CustomTheme from './CustomTheme';
import ClickableLink from './ClickableLink';
import CustomCursors from './CustomCursors';
import CustomImageUploader from './CustomImageUploader';
import { useCollaborativeEditor } from './useCollaborativeEditor';
import './index.style.less';

function convertFileToBase64(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result);
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsDataURL(file);
  });
}

// Register emoji icon so Quill renders it as inline SVG inside the toolbar button
const QuillIcons: Record<string, string> = Quill.import('ui/icons');
QuillIcons.emoji = `<svg viewBox="0 0 18 18"><circle class="ql-stroke" cx="9" cy="9" r="7"/><circle class="ql-fill" cx="6" cy="7" r="1"/><circle class="ql-fill" cx="12" cy="7" r="1"/><path class="ql-stroke" fill="none" d="M5.5,11 Q9,14.5 12.5,11"/></svg>`;

Quill.register(
  {
    'modules/autoLinks': AutoLinks,
    'modules/imageUploader': CustomImageUploader,
    'themes/snow': CustomTheme,
    'formats/link': ClickableLink,
    'modules/customCursors': CustomCursors,
  },
  true,
);

export interface RichTextEditorProps {
  id?: string;
  className?: string;
  wrapperClassName?: string;
  projectId?: string;
  placeholder?: string;
  collaborativeEditor?: CollaborativeEditorOptions;
  progressBar?: 'top' | 'bottom';
  readonly?: boolean;
  noContainer?: boolean;
  containerFullHeight?: boolean;
  includeMention?: boolean;
  includeEmoji?: boolean;
  includeToolbar?: boolean;
  showMoreButton?: boolean;
  imageAsData64?: boolean;
  mentionTypes?: ('regular_user' | 'freelancer' | 'contact' | 'group')[];
  mentionableUrl?: string;
  prefetchedMentionables?: any[];
  onChange?: ReactQuill.ReactQuillProps['onChange'];
  initialValue?: string | undefined;
  scrollingContainer?: string | HTMLElement | undefined;
  onImageUploader?: (upload: UploadFileApi) => Promise<void | string>;
  onLoadingImage?: (callback: (prev: number) => number) => void;
  onShortEnterKey?: (range: any, context: any) => void;
  onImageModalChanged?: (open: boolean) => void;
  onDragStateChange?: (state: 'allowed' | 'blocked' | null) => void;
  hideDropOverlay?: boolean;
  style?: CSSProperties;
  unstyled?: boolean;
}

export interface RichTextEditorHandle {
  getQuill(): ReactQuill | null;
  updateValue(text: string): void;
  getValue(): string | undefined;
  getYjs(): string;
  focus(): void;
  setMentionContents(mention: { id: number; name: string }): void;
  isEmpty(): boolean;
  hasPendingUploads(): boolean;
  waitForUploads(): Promise<void>;
}

export const editorStatusColor: Record<string, TextProps['type']> = {
  connecting: 'warning',
  saving: 'warning',
  saved: 'success',
  disconnected: 'danger',
};

const mentionGroupOrder = ['regular_user', 'contact', 'group'];

const buildMentionList = (rawData: any[], mentionTypes?: string[]): any[] => {
  const list: any[] = (
    mentionTypes
      ? rawData.filter((item: any) => mentionTypes.includes(item.type))
      : rawData
  ).map((el: any) => ({
    value: el.name,
    id: el.uuid,
    type: el.type,
  }));

  list.sort((a, b) => {
    const typeA = mentionGroupOrder.indexOf(a.type === 'freelancer' ? 'regular_user' : a.type);
    const typeB = mentionGroupOrder.indexOf(b.type === 'freelancer' ? 'regular_user' : b.type);
    return typeA - typeB;
  });

  if (!mentionTypes || mentionTypes.includes('regular_user') || mentionTypes.includes('freelancer')) {
    const firstUserIndex = list.findIndex((x) => x.type === 'regular_user' || x.type === 'freelancer');
    if (firstUserIndex >= 0) {
      list.splice(firstUserIndex, 0, { value: 'Users', disabled: true, isTitle: true });
    }
  }

  if (!mentionTypes || mentionTypes.includes('contact')) {
    const firstContactIndex = list.findIndex((x) => x.type === 'contact');
    if (firstContactIndex >= 0) {
      list.splice(firstContactIndex, 0, { value: 'Contacts', disabled: true, isTitle: true });
    }
  }

  if (!mentionTypes || mentionTypes.includes('group')) {
    const firstGroupIndex = list.findIndex((x) => x.type === 'group');
    if (firstGroupIndex >= 0) {
      list.splice(firstGroupIndex, 0, { value: 'Groups', disabled: true, isTitle: true });
    }
  }

  return list;
};

const debounceGetMention = debounceAsync<AxiosResponse>(
  (projectId: string | undefined, searchTerm: string, overrideUrl?: string) => {
    const url =
      overrideUrl ??
      (projectId
        ? `${apiRoutes.BASE_URL}/projects/${projectId}/mentionables`
        : `${apiRoutes.BASE_URL}/mentions/mentionables`);

    return apiRequests.get(url, {
      search_term: searchTerm || undefined,
    });
  },
  400,
);

const getMapImageProgressTotal = (map: Map<File, number>) => {
  let total = 0;

  // eslint-disable-next-line no-restricted-syntax
  for (const x of map.values()) {
    total += x;
  }

  return total / map.size;
};

const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  (
    {
      id,
      className = '',
      wrapperClassName,
      initialValue,
      projectId,
      placeholder,
      scrollingContainer = '.page-scrollbar',
      collaborativeEditor,
      mentionTypes,
      mentionableUrl,
      progressBar,
      readonly = false,
      noContainer = false,
      containerFullHeight = false,
      includeMention = false,
      includeEmoji = true,
      includeToolbar = true,
      showMoreButton = false,
      imageAsData64 = false,
      onImageUploader,
      onChange,
      onLoadingImage,
      onShortEnterKey,
      onImageModalChanged,
      onDragStateChange,
      hideDropOverlay = true,
      style,
      unstyled = false,
      prefetchedMentionables,
    },
    ref,
  ) => {
    const projectRef = useRef(projectId);
    const mentionUrlRef = useRef(mentionableUrl);
    const modulesRef = useRef<any>();
    const mentionTypesRef = useRef(mentionTypes);
    const includeMentionRef = useRef(includeMention);
    const cachedMentionablesRef = useRef<any[]>(prefetchedMentionables ?? []);
    const onShortEnterKeyRef = useRef(onShortEnterKey);
    const readOnlyRef = useRef<HTMLDivElement>(null);
    const quillRef = useRef<ReactQuill>(null);

    const emojiSearchRef = useRef<{ active: boolean; query: string; index: number }>({
      active: false, query: '', index: 0,
    });
    const toolbarPickerRef = useRef<HTMLDivElement>(null);
    const [inlineEmojiSearch, setInlineEmojiSearch] = useState({ active: false, query: '', index: 0 });
    const [hasInlineEmojiResults, setHasInlineEmojiResults] = useState(false);
    const [emojiPickerCoords, setEmojiPickerCoords] = useState({ top: 0, bottom: 0, left: 0 });
    const [toolbarEmojiOpen, setToolbarEmojiOpen] = useState(false);
    const toolbarEmojiOpenRef = useRef(false);

    const [value, setValue] = useState(() => {
      if (collaborativeEditor) {
        return undefined;
      }

      if (!initialValue) return undefined;

      try {
        const parsed = JSON.parse(initialValue);

        if (parsed?.ops) {
          if (readonly) {
            const Delta = Quill.import('delta');
            const delta = new Delta(parsed);
            const tempQuill = new Quill(document.createElement('div'));
            tempQuill.setContents(delta);
            return tempQuill.root.innerHTML;
          }

          return parsed;
        }

        return initialValue;
      } catch (e) {
        return initialValue;
      }
    });

    const [openLightbox, setOpenLightbox] = useState(false);
    const [listLightbox, setListLightbox] = useState<LightboxProps['slides']>([]);
    const [indexLightbox, setIndexLightbox] = useState(0);
    const [showAll, setShowAll] = useState(false);
    const [hasLargeContent, setHasLargeContent] = useState(true);
    const [mapImageProgress, setImageProgress] = useState(() => new Map<File, number>());
    const [dragoverState, setDragoverState] = useState<'allowed' | 'blocked' | null>(null);

    const containerEditorEl = quillRef.current?.getEditingArea();

    const editorAreaEl = useMemo(() => {
      return containerEditorEl?.querySelector('.ql-editor');
    }, [containerEditorEl]);

    const [editorHeight, setEditorHeight] = useState<number | undefined>(editorAreaEl?.clientHeight);

    const containerEditorHeight = containerEditorEl?.clientHeight;

    const { yDoc } = useCollaborativeEditor({ collaborativeEditor, quillRef, initialValue });

    if (projectRef.current !== projectId) {
      projectRef.current = projectId;
    }

    if (mentionTypesRef.current !== mentionTypes) {
      mentionTypesRef.current = mentionTypes;
    }

    if (includeMentionRef.current !== includeMention) {
      includeMentionRef.current = includeMention;
    }

    if (onShortEnterKeyRef.current !== onShortEnterKey) {
      onShortEnterKeyRef.current = onShortEnterKey;
    }

    if (prefetchedMentionables?.length && cachedMentionablesRef.current !== prefetchedMentionables) {
      cachedMentionablesRef.current = prefetchedMentionables;
    }

    const handleEmojiSelect = (emoji: { id: string; native: string }) => {
      const quill = quillRef.current?.getEditor();
      if (!quill) return;
      const { current } = emojiSearchRef;
      let index = quill.getSelection(true)?.index ?? quill.getLength();
      if (current.active) {
        quill.deleteText(current.index, current.query.length + 1); // +1 for ":"
        index = current.index;
      }
      quill.insertEmbed(index, 'emoji-inline', { id: emoji.id, native: emoji.native });
      quill.setSelection(index + 1, 0);
      const closed = { active: false, query: '', index: 0 };
      emojiSearchRef.current = closed;
      setInlineEmojiSearch(closed);
      setHasInlineEmojiResults(false);
    };

    if (!modulesRef.current && !readonly) {
      modulesRef.current = {
          autoLinks: true,
          customCursors: !!collaborativeEditor,
          history: {
            userOnly: true,
          },
          keyboard: {
            bindings: {
              ...(onShortEnterKey ? {
                sendWithEnter: {
                  key: 13,
                  shortKey: true,
                  handler: (range: any, context: any) => onShortEnterKeyRef.current?.(range, context),
                }
              } : {}),
            },
          },
          toolbar: includeToolbar
          ? (includeEmoji
            ? {
                container: [
                  [{ size: ['small', false, 'large', 'huge'] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  ['link', 'image'],
                  [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
                  [{ color: [] }, { background: [] }],
                  ['clean'],
                  ['emoji'],
                ],
                handlers: {
                  emoji: () => {
                    const closed = { active: false, query: '', index: 0 };
                    emojiSearchRef.current = closed;
                    setInlineEmojiSearch(closed);
                    setHasInlineEmojiResults(false);

                    if (toolbarEmojiOpenRef.current) {
                      // Already open: close immediately
                      setToolbarEmojiOpen(false);
                      return;
                    }

                    // Delay opening until after Quill's focus() scrolls to the cursor
                    setTimeout(() => {
                      const qEditor = quillRef.current?.getEditor();
                      if (!qEditor) return;

                      const range = qEditor.getSelection();
                      const containerRect = qEditor.container?.getBoundingClientRect();

                      if (range && containerRect) {
                        const bounds = qEditor.getBounds(range.index);
                        if (bounds) {
                          setEmojiPickerCoords({
                            top: containerRect.top + bounds.top,
                            bottom: containerRect.top + bounds.bottom,
                            left: containerRect.left + bounds.left,
                          });
                          setToolbarEmojiOpen(true);
                          return;
                        }
                      }

                      // Fallback: no selection — use toolbar button position
                      const btn = qEditor
                        .getModule('toolbar')?.container?.querySelector('.ql-emoji') as HTMLElement | null;
                      if (btn) {
                        const rect = btn.getBoundingClientRect();
                        setEmojiPickerCoords({ top: rect.top, bottom: rect.bottom, left: rect.left });
                      }
                      setToolbarEmojiOpen(true);
                    }, 0);
                  },
                },
              }
            : [
                [{ size: ['small', false, 'large', 'huge'] }],
                ['bold', 'italic', 'underline', 'strike'],
                ['link', 'image'],
                [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
                [{ color: [] }, { background: [] }],
                ['clean'],
              ])
          : undefined,
          mention: includeMention
            ? {
                allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
                dataAttributes: ['id', 'value', 'disabled', 'isTitle'],
                defaultMenuOrientation: 'top',
                mentionDenotationChars: ['@'],
                positioningStrategy: 'fixed',
                source: async (searchTerm: string, renderList: (list: []) => void) => {
                  if (!includeMentionRef.current) {
                    renderList([]);
                    return;
                  }

                  // Immediately render from cache (frontend filter) for instant UX
                  if (cachedMentionablesRef.current.length > 0) {
                    const filtered = cachedMentionablesRef.current.filter(
                      (el: any) => !searchTerm || el.name?.toLowerCase().includes(searchTerm.toLowerCase()),
                    );
                    renderList(buildMentionList(filtered, mentionTypesRef.current) as []);
                  }

                  // Background API call (debounced) — updates cache and re-renders with fresh data
                  try {
                    const res = await debounceGetMention(projectRef.current, searchTerm, mentionUrlRef.current);
                    cachedMentionablesRef.current = res.data.data;
                    renderList(buildMentionList(res.data.data, mentionTypesRef.current) as []);
                  } catch (error) {
                    if (cachedMentionablesRef.current.length === 0) renderList([]);
                    asyncErrorHandler(error);
                  }
                },
              }
            : undefined,
          imageUploader: {
            upload: (file: File) => {
              if (!/^image\//.test(file.type)) {
                return Promise.reject(new Error('You could only upload images.'))
                  .catch(() => {
                    // Suppress error and return empty string to avoid placeholder corruption.
                    // The capture/paste guards should prevent this path from executing,
                    // but this is a safety net if the guards are somehow bypassed.
                    return '';
                  });
              }
  
              const uploadFile = async () => {
                try {
                  if (progressBar) {
                    setImageProgress((old) => new Map<File, number>(old.set(file, 1)));
                  }
  
                  const response = await apiRequests.uploadFile({
                    file,
                    type: 'in-message',
                    onProgress: progressBar
                      ? (event) => {
                          setImageProgress((old) => {
                            old.set(file, event.percent > 90 ? 90 : event.percent);
                            return new Map<File, number>(old);
                          });
                        }
                      : undefined,
                  });
  
                  const newUrl = onImageUploader
                    ? await onImageUploader({
                        uid: response.uuid,
                        name: file.name,
                        status: 'success',
                        url: response.url,
                        size: file.size,
                        lastModified: file.lastModified,
                        lastModifiedDate: new Date(file.lastModified),
                        type: file.type,
                        percent: 100,
                        originFileObj: {
                          ...file,
                          lastModifiedDate: new Date(file.lastModified),
                          uid: response.uuid,
                        },
                        response,
                      })
                    : undefined;
  
                  if (progressBar) {
                    setImageProgress((old) => new Map<File, number>(old.set(file, 100)));
  
                    setTimeout(
                      () =>
                        setImageProgress((old) => {
                          old.delete(file);
                          return new Map<File, number>(old);
                        }),
                      500,
                    );
                  }
  
                  return newUrl ?? response.url;
                } finally {
                  onLoadingImage?.((prev) => prev - 1);
                }
              };
  
              if (onLoadingImage) {
                onLoadingImage((prev) => prev + 1);
              }
  
              if (imageAsData64) {
                uploadFile();
                return convertFileToBase64(file);
              }
  
              return uploadFile();
            },
          },
        };
      };

    useImperativeHandle(ref, () => ({
      updateValue(text: string) {
        if (!text) {
          setValue(undefined);
          return;
        }

        try {
          const parsed = JSON.parse(text);

          if (parsed?.ops) {
            if (readonly) {
              const Delta = Quill.import('delta');
              const delta = new Delta(parsed);
              const tempQuill = new Quill(document.createElement('div'));
              tempQuill.setContents(delta);
              setValue(tempQuill.root.innerHTML);
              return;
            }

            setValue(parsed);
            return;
          }

          setValue(text);
        } catch (e) {
          setValue(text);
        }
      },

      getQuill() {
        return quillRef.current;
      },

      getValue(): string | undefined {
        return value;
      },

      getYjs(): string {
        if (!yDoc) {
          return '';
        }

        return fromUint8Array(Y.encodeStateAsUpdate(yDoc));
      },

      focus() {
        quillRef.current?.focus();
      },

      isEmpty() {
        if (!quillRef.current) {
          return false;
        }

        const contents = quillRef.current.getEditor().getContents().ops;

        return !contents || !contents.find((x) => typeof x.insert !== 'string' || !!x.insert.trim());
      },

      setMentionContents({ id: mentionId, name }: { id: number; name: string }) {
        const editor = quillRef.current?.editor;

        if (editor && quillRef.current) {
          editor.setContents([
            {
              insert: {
                mention: {
                  id: mentionId,
                  value: name,
                  denotationChar: '@',
                },
              },
            },
            {
              insert: ' ',
            },
          ] as any);

          const html = editor.root.innerHTML;
          quillRef.current.value = html;
          setValue(html);

          quillRef.current.setEditorSelection(editor, { index: 2, length: 0 });
        }
      },

      hasPendingUploads() {
        return mapImageProgress.size > 0;
      },

      waitForUploads() {
        if (mapImageProgress.size === 0) return Promise.resolve();
        return new Promise<void>((resolve) => {
          const interval = setInterval(() => {
            if (mapImageProgress.size === 0) {
              clearInterval(interval);
              resolve();
            }
          }, 200);
        });
      },
    }));

    const onOpenLightboxHandler: MouseEventHandler<HTMLDivElement> = (event) => {
      const element = event.target;

      if (!isImgElement(element)) {
        return;
      }

      const listImages: LightboxProps['slides'] = [];

      const parser = new DOMParser();

      const doc = parser.parseFromString(value || '', 'text/html');

      const images = doc.querySelectorAll('img');

      images.forEach((image) => {
        listImages.push({ src: image.src });

        if (element.src === image.src) {
          setIndexLightbox(listImages.length - 1);
        }
      });

      onImageModalChanged?.(true);

      setListLightbox(listImages);

      setOpenLightbox(true);
    };

    useLayoutEffect(() => {
      if (!editorAreaEl || !showMoreButton) return () => {};

      const resizeObserver = new ResizeObserver((entries) => {
        const element = entries[0];

        setEditorHeight(element.target.clientHeight);
      });

      resizeObserver.observe(editorAreaEl);

      return () => {
        resizeObserver.disconnect();
      };
    }, [editorAreaEl, showMoreButton]);

    useEffect(() => {
      const editor = quillRef.current?.getEditor();

      if (!containerEditorEl || !editor) {
        return () => {};
      }

      const openLightboxHandler = (event: Event) => {
        const element = event.target;

        if (isImgElement(element) && editor) {
          const { ops } = editor.getContents();

          const listImages: LightboxProps['slides'] = [];

          ops?.forEach((delta) => {
            if (delta.insert.image) {
              listImages.push({
                src: delta.insert.image,
              });
            }

            if (element.src === delta.insert.image) {
              setIndexLightbox(listImages.length - 1);
            }
          });

          onImageModalChanged?.(true);

          setListLightbox(listImages);

          setOpenLightbox(true);
        }
      };

      containerEditorEl.addEventListener('click', openLightboxHandler);

      const focusHandler = (event: Event) => {
        if (event.target !== containerEditorEl || !quillRef.current) {
          return;
        }

        editor.setSelection(editor.getLength(), 0);
      };

      containerEditorEl.addEventListener('click', focusHandler);

      return () => {
        containerEditorEl.removeEventListener('click', openLightboxHandler);
        containerEditorEl.removeEventListener('click', focusHandler);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerEditorEl]);

    useEffect(() => {
      const editor = quillRef.current?.getEditor();
      if (!containerEditorEl || !editor) return () => {};

      const preventNonImageDrop = (event: DragEvent) => {
        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) return;

        const hasNonImage = Array.from(files).some(
          (file) => !/^image\//.test(file.type),
        );

        if (hasNonImage) {
          event.stopPropagation();
          event.preventDefault();
        }

        // Reset dragover state on drop
        setDragoverState(null);
      };

      const preventNonImagePaste = (event: ClipboardEvent) => {
        const files = event.clipboardData?.files;
        if (!files || files.length === 0) return;

        const hasNonImage = Array.from(files).some(
          (file) => !/^image\//.test(file.type),
        );

        if (hasNonImage) {
          event.stopPropagation();
          event.preventDefault();
        }
      };

      const handleDragOver = (event: DragEvent) => {
        // Use .items (available during dragover), not .files (always empty during dragover)
        const items = Array.from(event.dataTransfer?.items ?? []);
        const fileItems = items.filter((item) => item.kind === 'file');
        if (fileItems.length === 0) return;

        const hasNonImage = fileItems.some(
          (item) => !/^image\//.test(item.type),
        );

        event.preventDefault();
        event.stopPropagation();
        // Set dropEffect to indicate to the browser whether drop is allowed
        if (event.dataTransfer) {
          event.dataTransfer.dropEffect = hasNonImage ? 'none' : 'copy';
        }
        setDragoverState(hasNonImage ? 'blocked' : 'allowed');
      };

      const handleDragLeave = (event: DragEvent) => {
        // Reset dragover state when leaving the editor
        const rect = containerEditorEl.getBoundingClientRect();
        const x = (event as any).clientX;
        const y = (event as any).clientY;

        // If the cursor is outside the editor bounds, reset the state
        if (
          x < rect.left
          || x > rect.right
          || y < rect.top
          || y > rect.bottom
        ) {
          setDragoverState(null);
        }
      };

      // capture: true fires before quill-image-uploader's bubbling listener on quill.root
      containerEditorEl.addEventListener('drop', preventNonImageDrop as EventListener, true);
      containerEditorEl.addEventListener('paste', preventNonImagePaste as EventListener, true);
      containerEditorEl.addEventListener('dragover', handleDragOver as EventListener, false);
      containerEditorEl.addEventListener('dragleave', handleDragLeave as EventListener, false);

      return () => {
        containerEditorEl.removeEventListener('drop', preventNonImageDrop as EventListener, true);
        containerEditorEl.removeEventListener('paste', preventNonImagePaste as EventListener, true);
        containerEditorEl.removeEventListener('dragover', handleDragOver as EventListener, false);
        containerEditorEl.removeEventListener('dragleave', handleDragLeave as EventListener, false);
      };
    }, [containerEditorEl]);

    useEffect(() => {
      if (!hideDropOverlay) {
        onDragStateChange?.(dragoverState);
      }
    }, [dragoverState, onDragStateChange, hideDropOverlay]);

    useEffect(() => {
      const container = readOnlyRef.current;

      if (!container) return () => {};

      const openLightboxHandler = (event: Event) => {
        const element = event.target;

        if (isImgElement(element)) {
          const images = container.querySelectorAll('img');
          const listImages: LightboxProps['slides'] = [];

          images?.forEach((img) => {
            if (img.src) {
              listImages.push({
                src: img.src,
              });
            }

            if (element.src === img.src) {
              setIndexLightbox(listImages.length - 1);
            }
          });

          onImageModalChanged?.(true);

          setListLightbox(listImages);

          setOpenLightbox(true);
        }
      };

      container.addEventListener('click', openLightboxHandler);

      return () => {
        container.removeEventListener('click', openLightboxHandler);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [readOnlyRef]);

    useEffect(() => {
      if (showMoreButton && hasLargeContent && !showAll && quillRef.current?.editor?.hasFocus()) {
        setShowAll(true);
      }
    }, [showMoreButton, hasLargeContent, showAll, value]);

    useLayoutEffect(() => {
      if (!showMoreButton || !containerEditorHeight || !editorHeight) return;

      if (editorHeight >= containerEditorHeight) {
        if (!hasLargeContent) {
          setHasLargeContent(true);
        }

        return;
      }

      if (hasLargeContent) {
        setHasLargeContent(false);
      }
    }, [hasLargeContent, showAll, showMoreButton, editorHeight, containerEditorHeight]);

    useEffect(() => {
      toolbarEmojiOpenRef.current = toolbarEmojiOpen;
      if (!toolbarEmojiOpen) return () => {};
      const handleClickOutside = (e: MouseEvent) => {
        const btn = quillRef.current?.getEditor()
          .getModule('toolbar')?.container?.querySelector('.ql-emoji');
        if ((btn as HTMLElement | null)?.contains(e.target as Node)) return;
        if (toolbarPickerRef.current?.contains(e.target as Node)) return;
        setToolbarEmojiOpen(false);
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [toolbarEmojiOpen]);

    return (
      <>
        {!readonly || collaborativeEditor ? (
          <div 
            className={wrapperClassName} 
            style={{ position: 'relative' }} 
            onKeyDownCapture={(e) => {
              if (e.code === 'Enter' || e.code === 'NumpadEnter') {
                const isEmojiPickerOpen = !!document.querySelector(`[class*="listWrapper"]`);
          
                if (isEmojiPickerOpen) {
                  e.preventDefault();
                }
              }
            }}
          >
            {progressBar === 'top' && mapImageProgress.size > 0 && (
              <Progress
                className="quill-image-progress-bar"
                percent={getMapImageProgressTotal(mapImageProgress)}
                size="small"
                format={() => null}
                style={{ top: 0 }}
              />
            )}

            {dragoverState && !hideDropOverlay && (
              <div className={`ql-drag-overlay ql-drag-overlay--${dragoverState}`} />
            )}

            <ReactQuill
              id={id}
              ref={quillRef}
              readOnly={readonly}
              theme="snow"
              scrollingContainer={scrollingContainer}
              className={`${className}${containerFullHeight ? ' quill-container-h-full' : ''}${
                !includeToolbar ? ' no-toolbar' : ''
              }${noContainer ? ' no-container' : ''}${showMoreButton ? ' quill-has-more-button' : ''}${
                showMoreButton && !showAll && hasLargeContent ? ' quill-reduced-size' : ''
              }`}
              modules={modulesRef.current}
              placeholder={placeholder}
              value={value || initialValue || ''}
              onChange={(content, delta, source, editor) => {
                setValue(content);

                if (includeEmoji && source === 'user') {
                  const lastOp = (delta.ops || []).at(-1);
                  const { current } = emojiSearchRef;

                  if (lastOp?.insert === ':') {
                    const range = editor.getSelection();
                    if (range) {
                      const colonIndex = range.index - 1;
                      const charBefore = colonIndex > 0 ? editor.getText(colonIndex - 1, 1) : '';
                      if (/^(\s|\uFEFF|\uFFFC|)$/.test(charBefore)) {
                        const qEditor = quillRef.current?.getEditor();
                        const containerRect = qEditor?.container?.getBoundingClientRect();
                        const bounds = editor.getBounds(range.index);
                        if (containerRect && bounds) {
                          setEmojiPickerCoords({
                            top: containerRect.top + bounds.top,
                            bottom: containerRect.top + bounds.top + bounds.height,
                            left: containerRect.left + bounds.left,
                          });
                        }
                        const next = { active: true, query: '', index: colonIndex };
                        emojiSearchRef.current = next;
                        setInlineEmojiSearch(next);
                        setHasInlineEmojiResults(true);
                        setToolbarEmojiOpen(false);
                      }
                    }
                  } else if (current.active) {
                    const range = editor.getSelection();
                    if (!range || range.index <= current.index) {
                      const closed = { active: false, query: '', index: 0 };
                      emojiSearchRef.current = closed;
                      setInlineEmojiSearch(closed);
                      setHasInlineEmojiResults(false);
                    } else {
                      const textSinceColon = editor.getText(
                        current.index + 1,
                        range.index - (current.index + 1),
                      );
                      if (textSinceColon.includes(' ') || textSinceColon.includes('\n')) {
                        const closed = { active: false, query: '', index: 0 };
                        emojiSearchRef.current = closed;
                        setInlineEmojiSearch(closed);
                        setHasInlineEmojiResults(false);
                      } else {
                        const updated = { ...current, query: textSinceColon };
                        emojiSearchRef.current = updated;
                        setInlineEmojiSearch(updated);
                      }
                    }
                  }
                }

                onChange?.(content, delta, source, editor);
              }}
              style={style}
            />

            {includeEmoji && toolbarEmojiOpen && (
              <PickerPositioner top={emojiPickerCoords.top} anchorBottom={emojiPickerCoords.bottom} left={emojiPickerCoords.left}>
                <div ref={toolbarPickerRef}>
                  <EmojiPicker onSelect={handleEmojiSelect} />
                </div>
              </PickerPositioner>
            )}

            {includeEmoji && inlineEmojiSearch.active && (
              <PickerPositioner top={emojiPickerCoords.top} anchorBottom={emojiPickerCoords.bottom} left={emojiPickerCoords.left} visible={hasInlineEmojiResults}>
                <EmojiPicker
                  onSelect={handleEmojiSelect}
                  searchQuery={inlineEmojiSearch.query}
                  inline
                  onResultsCountChange={(count) => setHasInlineEmojiResults(count > 0)}
                  onClose={() => {
                    const closed = { active: false, query: '', index: 0 };
                    emojiSearchRef.current = closed;
                    setInlineEmojiSearch(closed);
                    setHasInlineEmojiResults(false);
                  }}
                />
              </PickerPositioner>
            )}

            {showMoreButton && hasLargeContent && (
              <div className={`quill-content-show${!showAll ? ' quill-content-show--more' : ''}`}>
                <Button type="link" onClick={() => setShowAll(!showAll)}>
                  {!showAll ? 'Show more' : 'Show less'}
                </Button>
              </div>
            )}

            {progressBar === 'bottom' && mapImageProgress.size > 0 && (
              <Progress
                className="quill-image-progress-bar"
                percent={getMapImageProgressTotal(mapImageProgress)}
                size="small"
                format={() => null}
                style={{ bottom: 0 }}
              />
            )}
          </div>
        ) : (
          <div className={wrapperClassName} style={{ position: 'relative' }}>
            <div
              ref={readOnlyRef}
              className={`quill ${className} ${noContainer ? 'no-container' : ''} ${
                showMoreButton ? 'quill-has-more-button' : ''
              } ${showMoreButton && !showAll && hasLargeContent ? 'quill-reduced-size' : ''}`}
              style={style}
            >
              <div className={unstyled ? '' : 'ql-container ql-snow'}>
                <div
                  className="ql-editor"
                  dangerouslySetInnerHTML={{
                    __html: value || '',
                  }}
                  style={unstyled ? { padding: 0 } : {}}
                  onClick={onOpenLightboxHandler}
                />
              </div>
            </div>

            {showMoreButton && hasLargeContent && (
              <div className={`quill-content-show${!showAll ? ' quill-content-show--more' : ''}`}>
                <Button type="link" onClick={() => setShowAll(!showAll)}>
                  {!showAll ? 'Show more' : 'Show less'}
                </Button>
              </div>
            )}
          </div>
        )}

        <Lightbox
          open={openLightbox}
          index={indexLightbox}
          close={() => {
            onImageModalChanged?.(false);
            setOpenLightbox(false);
          }}
          plugins={[Counter as any, Thumbnails, Zoom]}
          // @ts-ignore
          counter={{ container: { style: { top: 'unset', bottom: 0 } } }}
          zoom={{ maxZoomPixelRatio: 4 }}
          carousel={{ finite: true }}
          slides={listLightbox}
        />
      </>
    );
  },
);

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
